/**
 * Whole Life LOC - Benefit Definitions
 *
 * Provides field metadata and cell specifications for the Whole Life coverage grid.
 * These definitions drive labels, descriptions, editors, and validation for each benefit.
 */

import {
  addNotDisclosed,
  BaseBenefitPlanPolicyPeriod,
  BaseWholeLifeBenefitCoverageType,
  BaseWholeLifeBenefitEmployeeBenefitMaximumType,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  NotDisclosed,
  NotOffered,
  RateGuaranteeTypeEnum,
  RfpInclusionStatusEnum,
  RfpWholeLifeBenefitCoverageEffectiveDate,
  RfpWholeLifeBenefitDependentBenefitType,
  RfpWholeLifeBenefitEmployeeBenefitAmountType,
} from "@platform/openapi-types";

import { BenefitDefinitions } from "../../benefits/benefit.types";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildTypeAmountBenefitPair,
  replaceMultipleOptionsWithFrontendTypes,
  buildCommissionBenefits,
} from "../shared/definitions/builders";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  // Coverage Opportunity header: Current Carrier
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },

  // ========================================
  // COMMISSIONS
  // ========================================

  ...commissionDefs,
  // Override: RFP requires commission type fields to be required
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },

  // Eligibility Classes (benefit plan group level)
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },

  // Coverage Type
  coverageType: {
    label: "Coverage Type",
    description:
      "Indicates the type of coverage provided by the whole life plan",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(BaseWholeLifeBenefitCoverageType),
      },
    },

    networkDependent: false,
  },

  // Employee Benefit Amount (type + amount)
  employeeBenefitAmountType: {
    label: "Benefit Amount - Employee",
    description: "Describes the benefit configuration available",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosed(
          replaceMultipleOptionsWithFrontendTypes(
            Object.values(RfpWholeLifeBenefitEmployeeBenefitAmountType),
            false, // salaryMultiplier already in enum
          ),
        ),
      },
    },
    networkDependent: false,
    refreshesCells: ["employeeBenefitAmountAmount"],
  },
  employeeBenefitAmountAmount: {
    label: "Amount",
    description:
      "The dollar amounts for the benefit. One value unless type is multipleOptions",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 1,
    editableWhen: {
      dependsOn: "employeeBenefitAmountType",
      disabledValues: [NotDisclosed],
    },
    cellVariants: {
      dependsOn: "employeeBenefitAmountType",
      options: {
        multipleOptions: sharedCellSpecs.multipleAmounts,
        multipleDollarAmounts: sharedCellSpecs.multipleDollarAmountsPopover,
        salaryMultiplier: {
          ...sharedCellSpecs.salaryMultiplier,
          defaultValue: 1,
        },
        multipleSalaryMultipliers:
          sharedCellSpecs.multipleSalaryMultipliersPopover,
      },
      default: {
        ...sharedCellSpecs.dollarAmount,
        defaultValue: 1,
      },
    },
  },

  // Employee Benefit Maximum (type + dollar amount + salary multiplier)
  employeeBenefitMaximumType: {
    label: "Benefit Maximum - Employee",
    description:
      "Indicates the maximum amount of benefit the employee can elect",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(
          BaseWholeLifeBenefitEmployeeBenefitMaximumType,
        ),
      },
    },
    networkDependent: false,
    refreshesCells: [
      "employeeBenefitMaximumDollarAmount",
      "employeeBenefitMaximumSalaryMultiplier",
    ],
  },
  employeeBenefitMaximumDollarAmount: {
    label: "Dollar Amount",
    description: "Maximum benefit dollar amount",
    cell: {
      ...sharedCellSpecs.dollarAmount,
      defaultValue: 1,
    },
    defaultValue: 1,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "employeeBenefitMaximumType",
      disabledValues: ["", "notIncluded", "salaryMultiplier", NotDisclosed],
    },
  },
  employeeBenefitMaximumSalaryMultiplier: {
    label: "Salary Multiplier",
    description: "Maximum benefit salary multiplier",
    cell: {
      ...sharedCellSpecs.salaryMultiplier,
      defaultValue: 1,
    },
    defaultValue: 1,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "employeeBenefitMaximumType",
      disabledValues: ["", "notIncluded", "dollarAmount", NotDisclosed],
    },
  },

  // Spouse Benefit Amount
  ...buildTypeAmountBenefitPair({
    prefix: "spouseBenefitAmount",
    typeLabel: "Benefit Amount - Spouse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpWholeLifeBenefitDependentBenefitType,
    ),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates the amount of benefit available for a dependent spouse",
    amountDescription: "Spouse benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    revertAmountOnInvalid: true,
  }),

  // Spouse Benefit Maximum
  ...buildTypeAmountBenefitPair({
    prefix: "spouseBenefitMaximum",
    typeLabel: "Benefit Maximum - Spouse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpWholeLifeBenefitDependentBenefitType,
    ),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates the maximum amount of benefit the spouse can elect",
    amountDescription: "Spouse maximum benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    revertAmountOnInvalid: true,
  }),

  // Child Benefit Amount
  ...buildTypeAmountBenefitPair({
    prefix: "childBenefitAmount",
    typeLabel: "Benefit Amount - Child",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpWholeLifeBenefitDependentBenefitType,
    ),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates the amount of benefit available for a dependent child",
    amountDescription: "Child benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    revertAmountOnInvalid: true,
  }),

  // Child Benefit Maximum
  ...buildTypeAmountBenefitPair({
    prefix: "childBenefitMaximum",
    typeLabel: "Benefit Maximum - Child",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpWholeLifeBenefitDependentBenefitType,
    ),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates the maximum amount of benefit the child can elect",
    amountDescription: "Child maximum benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    revertAmountOnInvalid: true,
  }),

  // Guarantee Issue Amount (single value)
  guaranteeIssueAmount: {
    label: "Whole Life Guarantee Issue Amount",
    description: "The amount of whole life guarantee issue amount",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },

  // Riders Included (long text)
  ridersIncluded: {
    label: "Riders Included",
    description:
      "Indicates what riders may be available on the plan (waiver, AD&D, accelerated, etc.)",
    cell: sharedCellSpecs.longText,
    networkDependent: false,
    validation: { maxLength: 4096 },
  },

  // Additional Options Included (long text)
  additionalOptionsIncluded: {
    label: "Additional Options Included",
    description:
      "Indicates any plan specific provisions that could be available",
    cell: sharedCellSpecs.longText,
    networkDependent: false,
    validation: { maxLength: 4096 },
  },

  // Portability
  portability: {
    label: "Portability",
    description: "Ability to continue coverage after employment ends",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },

  // Interest Rate (percentage)
  interestRate: {
    label: "Interest Rate",
    description:
      "Indicates the interest rate applied to the plan (rate of growth)",
    cell: sharedCellSpecs.percentageAmount,
    isRequired: true,
    networkDependent: false,
  },

  // Endows Age (number up to 120)
  endowsAge: {
    label: "Endows Age",
    description: "The age at which the policy endows",
    cell: sharedCellSpecs.numberField,
    isRequired: true,
    networkDependent: false,
    validation: { maximum: 120 },
  },

  // Coverage Effective Date
  coverageEffectiveDate: {
    label: "Coverage Effective Date",
    description: "Indicates when the plan will be initiated",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpWholeLifeBenefitCoverageEffectiveDate,
        ),
      },
    },

    networkDependent: false,
  },

  // Annual Open Enrollment (shared cell spec)
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Type of annual open enrollment available",
    cell: sharedCellSpecs.annualOpenEnrollmentType,
    networkDependent: false,
  },

  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },

  // Rate Guarantee - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),

  // Extended (non-v5) field — CD-2072/CD-390
  longTermCare: {
    mpConfigKey: "long_term_care",
    mpOnly: true,
    label: "Long Term Care",
    description: "Long term care benefit details for the whole life plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
};
